/**
 * `ConnectedAppsPanel` — Account → Connected apps tab (ADR-0261 §5).
 *
 * Shows the merchant how to connect Claude / ChatGPT / Claude Code to
 * their Sill account over the new assistant MCP endpoint, lists the
 * currently-live grants from `GET /v1/oauth/grants`, and lets them
 * revoke any one with `DELETE /v1/oauth/grants/:grant_id`.
 *
 * The connection URL is shown exactly as the server expects it; there
 * is no per-site variant here (unlike the per-site MCP endpoint under
 * Settings → Install, which is for AGENTS shopping the site). An
 * assistant connection is an account/user credential — see the
 * pointer caption on Settings → Install, which deep-links back to
 * this tab.
 *
 * Hidden while acting (Account.tsx's `HIDDEN_WHILE_ACTING`) because
 * consent lives on the home session. Under a read-only support
 * session the http-client already maps writes to a 403 with the
 * `support_session_read_only` reason; the Revoke button becomes a
 * disabled affordance via the same error map Account tabs use.
 */

import { useCallback, useEffect, useState } from 'react';

import type { ConnectedAppGrant } from '@sill/types/api';

import { useOptionalHttp } from '../../api/http-context.js';
import {
  SCOPE_CHIP_LABELS,
  listConnectedApps,
  revokeConnectedApp,
} from '../../api/connected-apps.js';
import { useC } from '../../lib/theme.js';
import { Button, Card, ConfirmDialog, Pill, RelativeTime, Tooltip } from '../../components/atoms/index.js';
import { ConnectedAppCallLog } from './ConnectedAppCallLog.js';
import { ConnectedAppsSetupSteps } from './ConnectedAppsSetupSteps.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

/** Public server URL — pinned to the production host per ADR-0261 §2.4. */
const ASSISTANT_MCP_URL = 'https://api.sill.so/mcp';

type LoadState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly grants: readonly ConnectedAppGrant[]; readonly dcr_enabled: boolean }
  | { readonly kind: 'disabled' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'error'; readonly message: string };

export function ConnectedAppsPanel(): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [pendingRevoke, setPendingRevoke] = useState<string | null>(null);
  // The connection whose call log is open, or null.
  const [logFor, setLogFor] = useState<ConnectedAppGrant | null>(null);
  // `?revoke=og_…` from the new-connection email: open the revoke
  // confirmation for that connection once the list has loaded. Read
  // once and consumed from the URL so a reload does not re-prompt.
  const [revokeDeepLink, setRevokeDeepLink] = useState<string | null>(() => readRevokeDeepLink());

  const load = useCallback(async (): Promise<void> => {
    if (http === null) return;
    setState({ kind: 'loading' });
    const r = await listConnectedApps(http);
    if (r.kind === 'ok') setState({ kind: 'ready', grants: r.response.grants, dcr_enabled: r.response.dcr_enabled === true });
    else if (r.kind === 'disabled') setState({ kind: 'disabled' });
    else if (r.kind === 'forbidden') setState({ kind: 'forbidden' });
    else setState({ kind: 'error', message: r.message });
  }, [http]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="flex flex-col gap-5">
      <Card className="p-5 sm:p-6" data-testid="connected-apps-intro">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 2,
            textTransform: 'uppercase',
            color: C.muted,
            marginBottom: 10,
          }}
        >
          Assistants
        </div>
        <h2
          style={{
            fontFamily: serifFont,
            fontSize: 24,
            lineHeight: 1.1,
            color: C.text,
            letterSpacing: -0.5,
            margin: 0,
          }}
        >
          Use Sill from your assistant.
        </h2>
        <p
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.muted,
            lineHeight: 1.6,
            marginTop: 12,
            maxWidth: 640,
          }}
        >
          Connect Claude, ChatGPT, Claude Code or another assistant that
          supports MCP with OAuth sign-in. Each connection acts as you, with
          only the permissions you approve.
          Connections expire after 90 days; the assistant asks to reconnect.
        </p>

        <div className="mt-5">
          <ServerUrlRow url={ASSISTANT_MCP_URL} />
        </div>

        <div className="mt-6">
          <ConnectedAppsSetupSteps
            server_url={ASSISTANT_MCP_URL}
            other_assistants_enabled={state.kind === 'ready' && state.dcr_enabled}
          />
        </div>
      </Card>

      <Card className="p-5 sm:p-6" data-testid="connected-apps-list">
        <div className="flex items-baseline justify-between gap-4 mb-4">
          <h3
            style={{
              fontFamily: serifFont,
              fontSize: 20,
              color: C.text,
              letterSpacing: -0.3,
              margin: 0,
            }}
          >
            Connected assistants
          </h3>
          {state.kind === 'ready' && state.grants.length > 0 ? (
            <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
              {state.grants.length}
            </span>
          ) : null}
        </div>

        {state.kind === 'loading' ? (
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
            Loading connected apps…
          </div>
        ) : null}

        {state.kind === 'error' ? (
          <div
            data-testid="connected-apps-error"
            style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg, lineHeight: 1.5 }}
          >
            {state.message}
          </div>
        ) : null}

        {state.kind === 'disabled' ? (
          <div
            data-testid="connected-apps-disabled"
            style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}
          >
            Connecting assistants is not available on this account yet.
          </div>
        ) : null}

        {state.kind === 'forbidden' ? (
          <div
            data-testid="connected-apps-forbidden"
            style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}
          >
            Sign in with your own account to manage connected assistants.
          </div>
        ) : null}

        {state.kind === 'ready' && state.grants.length === 0 ? (
          <div
            data-testid="connected-apps-empty"
            style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}
          >
            No assistants connected yet. Add the URL above to your assistant
            to start.
          </div>
        ) : null}

        {state.kind === 'ready' && state.grants.length > 0 ? (
          <ConnectionsList
            grants={state.grants}
            onOpenLog={setLogFor}
            initialRevoke={revokeDeepLink}
            onInitialRevokeConsumed={() => setRevokeDeepLink(null)}
            pendingRevoke={pendingRevoke}
            onRevoke={async (grant_id) => {
              if (http === null) return;
              setPendingRevoke(grant_id);
              const r = await revokeConnectedApp(http, grant_id);
              setPendingRevoke(null);
              if (r.kind === 'ok' || r.kind === 'not_found') {
                // Treat not_found as a race (already revoked) — refetch
                // closes the row either way.
                await load();
                return;
              }
              // Non-ok outcomes surface in-row via `setState('error')` so
              // the user can retry. Keep the row visible.
              const message = r.kind === 'forbidden'
                ? 'This session cannot revoke connections.'
                : r.message;
              setState({ kind: 'error', message });
            }}
          />
        ) : null}
      </Card>
      {logFor !== null ? <ConnectedAppCallLog grant={logFor} onClose={() => setLogFor(null)} /> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function ServerUrlRow({ url }: { readonly url: string }): JSX.Element {
  const C = useC();
  const [copied, setCopied] = useState(false);
  const copy = (): void => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(url);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-x-4 gap-y-1">
      <div
        className="sm:self-center"
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 2,
          textTransform: 'uppercase',
          color: C.muted,
        }}
      >
        Server URL
      </div>
      <div
        className="px-2.5 py-1.5 flex items-center gap-2"
        style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}
        data-testid="connected-apps-server-url"
      >
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 12,
            color: C.text,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {url}
        </span>
        <button
          type="button"
          aria-label="Copy assistant MCP server URL"
          onClick={copy}
          className="flex-shrink-0 px-2 py-1"
          style={{
            background: C.bg,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            fontFamily: sansFont,
            fontSize: 11,
            color: C.muted,
            letterSpacing: 1,
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Expiry caption for the Created cell. Connections expire 90 days after
 * creation (ADR-0261 §8); the server does not emit a dedicated field so
 * the dashboard computes the hint from `created_at`. Invalid timestamps
 * degrade silently to an em-dash — the row is still useful without it.
 */
const CONNECTION_LIFETIME_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * ADR-0262 S3 — the usage part of the row's meta line: calls in the
 * last 7 and 30 days plus the last tool. Zero calls reads "no calls in
 * 30 days".
 */
export function formatGrantUsage(calls_7d: number, calls_30d: number, last_tool: string | null): string {
  if (calls_30d === 0) return 'no calls in 30 days';
  const head = `${calls_7d.toLocaleString('en-US')} calls in 7 d · ${calls_30d.toLocaleString('en-US')} in 30 d`;
  return last_tool === null ? head : `${head} · last ${last_tool}`;
}

export function formatExpiresDate(iso_created_at: string): string {
  const created = Date.parse(iso_created_at);
  if (!Number.isFinite(created)) return '—';
  const expires = new Date(created + CONNECTION_LIFETIME_MS);
  const year = expires.getUTCFullYear();
  const month = String(expires.getUTCMonth() + 1).padStart(2, '0');
  const day = String(expires.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const GRANT_ID_RE = /^og_[0-9A-Za-z]{26}$/;

/** Read + consume the optional `?revoke=<grant_id>` deep link from the new-connection email. */
export function readRevokeDeepLink(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get('revoke');
    if (raw === null) return null;
    url.searchParams.delete('revoke');
    window.history.replaceState(null, '', url.toString());
    return GRANT_ID_RE.test(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** Permission pills shown before the "+N more" toggle. Four fit one line at 390px. */
const CHIPS_COLLAPSED = 4;

/**
 * The connections list: one bordered row per grant in the sessions
 * style (SecurityPanel), not a column grid. Line 1 identifies the
 * connection and carries the two actions; line 2 is the permissions;
 * line 3 is one mono meta line with created / expires / last used /
 * usage. Revoke confirms in a dialog, so nothing ever renders inside
 * the row that could overlap its data (design review 2026-11-01).
 */
function ConnectionsList({
  grants,
  pendingRevoke,
  onRevoke,
  onOpenLog,
  initialRevoke = null,
  onInitialRevokeConsumed,
}: {
  readonly grants: readonly ConnectedAppGrant[];
  readonly pendingRevoke: string | null;
  readonly onRevoke: (grant_id: string) => Promise<void>;
  readonly onOpenLog: (grant: ConnectedAppGrant) => void;
  /** Grant id from the email's "Revoke this connection" link; opens its confirmation on mount. */
  readonly initialRevoke?: string | null;
  readonly onInitialRevokeConsumed?: () => void;
}): JSX.Element {
  const [confirming, setConfirming] = useState<ConnectedAppGrant | null>(null);
  useEffect(() => {
    if (initialRevoke === null) return;
    const g = grants.find((x) => x.grant_id === initialRevoke) ?? null;
    if (g !== null) setConfirming(g);
    onInitialRevokeConsumed?.();
    // The deep link is consumed once; a later list refresh must not re-open it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRevoke]);
  return (
    <div className="space-y-2" role="list" aria-label="Connected assistants">
      {grants.map((g) => (
        <GrantRow
          key={g.grant_id}
          grant={g}
          pending={pendingRevoke === g.grant_id}
          anyPending={pendingRevoke !== null}
          onRevoke={() => setConfirming(g)}
          onOpenLog={() => onOpenLog(g)}
        />
      ))}
      <ConfirmDialog
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        title={`Revoke ${confirming?.client_label ?? ''}?`}
        body="This connection stops working now. The assistant will ask you to connect again."
        confirmLabel="Revoke"
        cancelLabel="Keep connection"
        confirmTone="coral"
        onConfirm={() => {
          const g = confirming;
          setConfirming(null);
          if (g !== null) void onRevoke(g.grant_id);
        }}
      />
    </div>
  );
}

function GrantRow({
  grant,
  pending,
  anyPending,
  onRevoke,
  onOpenLog,
}: {
  readonly grant: ConnectedAppGrant;
  readonly pending: boolean;
  readonly anyPending: boolean;
  readonly onRevoke: () => void;
  readonly onOpenLog: () => void;
}): JSX.Element {
  const C = useC();
  const [expanded, setExpanded] = useState(false);
  const chips = grant.scopes.map((s) => ({ scope: s, label: SCOPE_CHIP_LABELS[s] ?? s }));
  const hidden = Math.max(0, chips.length - CHIPS_COLLAPSED);
  const shown = expanded ? chips : chips.slice(0, CHIPS_COLLAPSED);
  const account_label = grant.acting_account_id === null
    ? 'HOME ACCOUNT'
    : `CLIENT · ${grant.acting_account_id.slice(0, 8)}`;
  return (
    <div
      role="listitem"
      data-testid={`grant-row-${grant.grant_id}`}
      className="p-3"
      style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span style={{ fontFamily: sansFont, fontSize: 13, fontWeight: 500, color: C.text }}>{grant.client_label}</span>
            <span style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, overflowWrap: 'anywhere' }}>{grant.user_email}</span>
            <Pill tone="neutral">{account_label}</Pill>
          </div>
          <Tooltip content={grant.client_id} displayMode="block" style={{ minWidth: 0 }}>
            <div
              data-testid={`grant-address-${grant.grant_id}`}
              style={{ fontFamily: monoFont, fontSize: 10, color: C.dim, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {grant.client_id}
            </div>
          </Tooltip>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={onOpenLog}
            aria-label={`Call log for ${grant.client_label}`}
            data-testid={`grant-log-${grant.grant_id}`}
          >
            Call log
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={anyPending && !pending}
            loading={pending}
            onClick={onRevoke}
            aria-label={`Revoke access for ${grant.client_label}`}
          >
            Revoke
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1" style={{ marginTop: 8 }} data-testid={`grant-scopes-${grant.grant_id}`}>
        {shown.map((c) => (
          <Pill key={c.scope} tone="neutral">{c.label}</Pill>
        ))}
        {hidden > 0 ? (
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            data-testid={`grant-scopes-toggle-${grant.grant_id}`}
            style={{ background: 'transparent', border: 'none', padding: '0 4px', cursor: 'pointer', fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase' }}
          >
            {expanded ? 'Show fewer' : `+${hidden} more`}
          </button>
        ) : null}
      </div>

      <div
        data-testid={`grant-meta-${grant.grant_id}`}
        style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, marginTop: 8, wordBreak: 'break-word', lineHeight: 1.6 }}
      >
        created <RelativeTime iso={grant.created_at} />
        {' · '}
        <span data-testid={`grant-expires-${grant.grant_id}`}>expires {formatExpiresDate(grant.created_at)}</span>
        {' · last used '}
        {grant.last_used_at === null ? 'never' : <RelativeTime iso={grant.last_used_at} />}
        {typeof grant.calls_30d === 'number' ? (
          <span data-testid={`grant-usage-${grant.grant_id}`}>
            {' · '}
            {formatGrantUsage(grant.calls_7d ?? 0, grant.calls_30d, grant.last_tool ?? null)}
          </span>
        ) : null}
      </div>
    </div>
  );
}
